import type { HeadersFunction, LoaderFunction, MetaFunction } from '@remix-run/node';
import { redirect } from '@remix-run/node';
import { type ClientLoaderFunctionArgs, useOutletContext, useRouteLoaderData } from '@remix-run/react';
import * as Sentry from '@sentry/remix';
import type { BasicNode, TRootElement } from '@shared/dream-components';
import type { PostTheme } from '@shared/dream-components/src';

import { withLazyBlocks } from '~/components/DreamBuilder/lazyBlock';
import { useMetaViewContent } from '~/components/Pixels/useMetaViewContent';
import BlueskyEmbed from '~/components/Scripts/BlueskyEmbed';
import StocktwitsEmbed from '~/components/Scripts/StocktwitsEmbed';
import TwitterEmbed from '~/components/Scripts/TwitterEmbed';
import StaticPage from '~/components/StaticPage';
import { PostDataProvider } from '~/context/PostDataProvider';
import { PostPageContext } from '~/context/PostPageContext';
import { PostPageProvider } from '~/context/PostPageProvider';
import useCreatePageView from '~/hooks/useLoaders/useCreatePageView';
import type { CommentsPage } from '~/hooks/useLoaders/usePaginatedComments';
import useSetLastViewedResource from '~/hooks/useSetLastViewedResource';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { Author } from '~/interfaces/author';
import type { CommentType } from '~/interfaces/comment';
import type { OutletContext } from '~/interfaces/outlet_context';
import type { Page } from '~/interfaces/page';
import { type PaywallResponse, selectPaywall, selectStandardPaywall } from '~/interfaces/paywall';
import type { Post as IPost, PostMetaInformation } from '~/interfaces/post';
import type { PostMergeTags } from '~/interfaces/post_merge_tags';
import type { PostTemplate } from '~/interfaces/post_template';
import type { WebThemeV2 } from '~/interfaces/publication';
import type { Site } from '~/interfaces/site';
import type { Subscription } from '~/interfaces/subscriptions';
import type { RootLoaderData } from '~/root';
import getPage from '~/services/api/pages/getPage.server';
import getPaywall from '~/services/api/paywalls/getPaywall.server';
import getPostTemplate from '~/services/api/post_templates/getPostTemplate.server';
import getDraftPost from '~/services/api/posts/getDraftPost.server';
import getPaginatedPostComments from '~/services/api/posts/getPaginatedPostComments.server';
import getPost from '~/services/api/posts/getPost.server';
import getPreviewSubscription from '~/services/api/preview_subscription/getPreviewSubscription.server';
import { getPostThemeFontFamilies, getRenderableFontsCss } from '~/services/fonts';
import type { GoogleFontEntryType } from '~/services/fonts/cache';
import { loaderSession } from '~/session';
import { CONDITIONAL_MARKER_HEADER, isCacheableResponse } from '~/utils/documentValidator';
import getCanonicalUrl from '~/utils/getCanonicalUrl';
import getMergedMetaData from '~/utils/getMergedMetaData';
import getMetaDataLinks from '~/utils/getMetaDataLinks';
import getPersonSchema from '~/utils/getPersonSchema';
import getSameAsArray from '~/utils/getSameAsArray';
import { publicationFromMatches, siteFromMatches } from '~/utils/publicationFromLoaderData';
import resolveDateModified from '~/utils/resolveDateModified';
import setCacheHeaders, { BASE_CACHE_TAG } from '~/utils/setCacheHeaders/index.server';
import { shouldRevalidateLoaderData } from '~/utils/shouldRevalidateLoaderData';
import splitCustomStructuredData from '~/utils/splitCustomStructuredData';

import useClickTracking from './_hooks/useClickTracking';
import { fetchMergeTags } from './_utils/fetchMergeTags';
import { fetchPostMetaInformation } from './_utils/fetchPostMetaInformation';
import { processContentGate } from './_utils/processContentGate';

// shortcut: bigger threads keep the client skeleton because every Orchid page re-renders in full after hydration,
// which doubles their render cost; raise or drop it once that re-render is fixed.
const SERVER_RENDERED_COMMENTS_LIMIT = 30;
const countComments = (comments: CommentType[]): number =>
  comments.reduce((count, comment) => count + 1 + countComments(comment.sub_comments ?? []), 0);

const getSchemaType = (postSchemaType: string | null | undefined): string => {
  switch (postSchemaType) {
    case 'news':
      return 'NewsArticle';
    case 'blog':
      return 'BlogPosting';
    default:
      return 'Article';
  }
};

export const meta: MetaFunction<typeof loader> = ({ matches, data }) => {
  const post = data?.post;
  const publication = publicationFromMatches(matches);
  const requestUrl = data?.requestUrl || '';
  const schemaUrl = getCanonicalUrl(requestUrl);
  const postSchemaType = data?.postSchemaType;
  const authorName = post?.authors?.[0]?.name;
  const authorEntries = (post?.authors ?? []).map((author: Author) =>
    getPersonSchema(author, { url: post?.author_urls?.[author.id], publication }),
  );
  const customStructuredData = post && 'custom_structured_data' in post ? post.custom_structured_data : undefined;

  if (!post || !publication) {
    return [
      { title: '404 - Not Found' },
      { description: 'The newsletter platform built for growth.' },
      { keywords: 'Newsletter, Email, Growth, Platform, SaaS, Creator' },
    ];
  }

  const { articleOverride, appendedBlocks } = splitCustomStructuredData(customStructuredData);
  const datePublished = post?.override_scheduled_at || '';
  const dateModified = resolveDateModified({
    override: articleOverride.dateModified,
    updatedAt: post?.updated_at,
    datePublished,
  });

  return getMergedMetaData({
    canonicalUrl: schemaUrl,
    matches,
    returnEarlyCheck: !post,
    pageMetadata: data?.page?.viewable_page_version?.metadata as Record<string, unknown> | undefined,
    siteMetadata: siteFromMatches(matches)?.viewable_site_version?.metadata as Record<string, unknown> | undefined,
    overrides: {
      title: post?.meta_default_title || post?.web_title || '',
      description: post?.meta_default_description || post?.web_subtitle || '',
      'og:url': `${publication?.url || ''}p/${post?.slug || ''}`,
      'og:title': post?.meta_og_title || post?.meta_default_title || post?.web_title || '',
      'og:description': post?.meta_og_description || post?.meta_default_description || post?.web_subtitle || '',
      'og:site_name': publication?.name || '',
      'og:image': post?.image_url || publication?.thumbnail?.url || publication?.logo?.url || '',
      'og:image:alt': post?.web_title || post?.meta_default_title || '',
      'twitter:url': `${publication?.url || ''}p/${post?.slug || ''}`,
      'twitter:title': post?.meta_twitter_title || post?.meta_default_title || post?.web_title || '',
      'twitter:description':
        post?.meta_twitter_description || post?.meta_default_description || post?.web_subtitle || '',
      'twitter:image': post?.image_url || publication?.thumbnail?.url || publication?.logo?.url || '',
      ...(post?.authors?.[0]?.twitter_handle
        ? { 'twitter:creator': `@${post.authors[0].twitter_handle.replace(/^@/, '')}` }
        : {}),
      ...(authorName ? { author: authorName } : {}),
      'og:type': 'article',
      ...(authorName ? { 'article:author': authorName } : {}),
      ...(datePublished ? { 'article:published_time': datePublished } : {}),
      ...(dateModified ? { 'article:modified_time': dateModified } : {}),
    },
    additionalRobots: 'max-image-preview:large',
    links: getMetaDataLinks({ renderableFonts: data?.renderableFonts || [] }),
    structuredData: [
      {
        '@context': 'https://schema.org',
        '@type': getSchemaType(postSchemaType),
        identifier: post?.slug || '',
        url: schemaUrl,
        // mainEntityOfPage describes the webpage container, not the article content type.
        // This should always be 'WebPage' regardless of the article @type (Article/NewsArticle/BlogPosting).
        // See: https://developers.google.com/search/docs/appearance/structured-data/article
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': schemaUrl,
        },
        headline: post?.web_title || post?.meta_default_title || '',
        description: post?.web_subtitle || post?.meta_default_description || post?.meta_og_description || '',
        datePublished,
        isAccessibleForFree: post?.is_accessible_for_free ?? true,
        ...(post?.is_accessible_for_free === false
          ? {
              hasPart: {
                '@type': 'WebPageElement',
                isAccessibleForFree: false,
                cssSelector: '#content-blocks',
              },
            }
          : {}),
        image: post?.image_url
          ? {
              '@type': 'ImageObject',
              url: post.image_url,
            }
          : undefined,
        author: authorEntries.length === 0 ? undefined : authorEntries.length === 1 ? authorEntries[0] : authorEntries,
        publisher: publication
          ? {
              '@type': 'Organization',
              name: publication?.name || '',
              url: publication?.url || '',
              identifier: publication?.id || '',
              sameAs: getSameAsArray(publication),
              logo: publication?.logo?.url
                ? {
                    '@type': 'ImageObject',
                    url: publication.logo.url,
                    contentUrl: publication.logo.url,
                    thumbnailUrl: publication.logo?.thumb?.url,
                  }
                : undefined,
              image: publication?.logo?.url
                ? {
                    '@type': 'ImageObject',
                    url: publication.logo.url,
                    contentUrl: publication.logo.url,
                    thumbnailUrl: publication.logo?.thumb?.url,
                  }
                : undefined,
            }
          : undefined,
        ...articleOverride,
        dateModified,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@id': publication?.url || '',
              name: publication?.name || '',
            },
          },
          {
            '@type': 'ListItem',
            position: 2,
            item: {
              '@id': schemaUrl,
              name: post?.web_title || '',
            },
          },
        ],
      },
      ...appendedBlocks,
    ],
  });
};

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  const conditional = loaderHeaders.get(CONDITIONAL_MARKER_HEADER);

  return {
    'cache-control': loaderHeaders.get('cache-control') || 'max-age=0', // no-cache
    'Cache-Tag': loaderHeaders.get('Cache-Tag') || BASE_CACHE_TAG,
    ...(conditional ? { [CONDITIONAL_MARKER_HEADER]: conditional } : {}),
  };
};

export interface PostLoaderData {
  slugInParams: string;
  post: IPost | PostTemplate;
  postMergeTags?: PostMergeTags;
  page: Page;
  webTheme: WebThemeV2;
  commentsEnabled: boolean;
  recommendedReadingEnabled: boolean;
  requestUrl: string;
  onlyRenderComments: boolean;
  isDraft: boolean;
  isPostTemplatePreview: boolean;
  postTemplateId: string | null;
  paywall: PaywallResponse | null;
  currentSubscriberId: string;
  tierIds: string[];
  dynamicContentOverrides: string[];
  renderableFonts: string[];
  renderableFontsCss: GoogleFontEntryType[];
  adobeFontUrls: string[];
  subscription?: Subscription;
  previewSubscription?: Subscription;
  previewJwtToken?: string;
  postMetaInformation?: PostMetaInformation;
  firstCommentsPage: CommentsPage | null;
  // Content gate data
  isContentClipped?: boolean;
  clippedPageContent?: TRootElement | BasicNode[] | null;
  postSchemaType?: 'default' | 'news' | 'blog' | null;
}

export const loader: LoaderFunction = async ({ request, params, context }) => {
  const { session } = await loaderSession(request);
  const url = new URL(request.url);
  const showComments = url.searchParams.get('comments') === 'true';
  const isDraft = url.searchParams.get('draft') === 'true';
  const currentSubscriberId = url.searchParams.get('current_subscriber_id') || '';
  const tierIds = url.searchParams.getAll('tier_ids[]');
  const dynamicContentOverrides = url.searchParams.getAll('dynamic_content_overrides[]');
  // Swarm authorizes + sources the preview simulation from this token (minted by
  // Swarm for an authorized previewer); raw tier/override params are ignored
  // server-side unless backed by it.
  const previewJwtToken = url.searchParams.get('preview_jwt_token');
  const postTemplateId = url.searchParams.get('post_template_id');
  const isPostTemplatePreview = postTemplateId !== null;

  // For post templates, 'post_templates' is used as the special, constant slug
  if (!params.slug && !isPostTemplatePreview) {
    return redirect('/');
  }

  const fetchPost = (removeCookie: boolean) =>
    getPost(request, session, params.slug || '', undefined, true, { removeCookie });

  // The page lookup needs only the URL, so it runs alongside the post fetch instead of after it.
  const pagePromise = getPage(request, session, {
    pathname: 'p/$slug',
    ...(isPostTemplatePreview ? { postTemplateId } : { postSlug: params.slug }),
    draft: url.searchParams.get('draft') || undefined,
  });
  // Keeps a failed page lookup from becoming an unhandled rejection when the post fetch throws first.
  pagePromise.catch(() => undefined);

  // Keyed by slug so it runs alongside the post fetch; the post decides below whether the page uses it.
  const commentsPromise: Promise<CommentsPage | null> =
    isDraft || isPostTemplatePreview
      ? Promise.resolve(null)
      : context
          .getSite(request, session)
          .then((site) => {
            const { publication } = site;
            const settings = site.viewable_site_version?.settings;
            if (settings?.comments_enabled === false || (publication.community_enabled && publication.has_community)) {
              return null;
            }
            return getPaginatedPostComments(
              request,
              session,
              '',
              '1',
              undefined,
              settings?.comments_ordering,
              params.slug,
            );
          })
          .catch((error) => {
            Sentry.captureException(error);
            return null;
          });

  const promises: [Promise<Site>, Promise<IPost | PostTemplate>] = [
    context.getSite(request, session),
    (() => {
      if (isDraft && !isPostTemplatePreview) {
        return getDraftPost({
          request,
          session,
          slug: params.slug || '',
          currentSubscriberId,
          tierIds,
          dynamicContentOverrides,
          omitHtml: true,
          previewJwtToken,
        });
      }

      if (isPostTemplatePreview) {
        return getPostTemplate({
          request,
          session,
          postTemplateId: postTemplateId as string,
          currentSubscriberId,
          tierIds,
          dynamicContentOverrides,
          omitHtml: true,
          previewJwtToken,
        });
      }

      return fetchPost(true);
    })(),
  ];
  // SSR drops the member token so the document stays cacheable. A private publication 404s
  // without it, and its response is already no-store, so retry once with the token.
  const [site, post] = await Promise.all(promises).catch(async (error): Promise<[Site, IPost | PostTemplate]> => {
    const site = await context.getSite(request, session);
    const memberPostHidden = error instanceof Response && error.status === 404 && site.publication.private;
    if (!memberPostHidden || isDraft || isPostTemplatePreview) throw error;
    return [site, await fetchPost(false)];
  });
  const publication = site.publication;

  const isPremiumPost = post.audience === 'premium' || post.audience === 'both';

  // Post templates don't have a redirect property, only posts do
  if (!isPostTemplatePreview && 'redirect' in post && post.redirect) {
    return redirect('/'); // Redirects to index feed when no post is found
  }

  // Opted-in publications get the /email URL written into the email at send time
  // (see MergeTags::BuildSubstitutions#live_url_value). This redirect only covers
  // links minted before they opted in, and only when there's no web post to show.
  // Skip for draft/template/Swarm previews — those always use the web view.
  if (
    !isPostTemplatePreview &&
    !isDraft &&
    !previewJwtToken &&
    !site.viewable_site_version &&
    publication.read_online_email_version_enabled
  ) {
    const emailUrl = new URL(`/p/${params.slug}/email`, url.origin);
    const jwtToken = url.searchParams.get('jwt_token');
    if (jwtToken) emailUrl.searchParams.set('jwt_token', jwtToken);

    return redirect(emailUrl.pathname + emailUrl.search, { status: 302 });
  }

  // The cached document is the same for every reader, so it only carries comments any reader sees on first render:
  // premium posts hide them from non-premium readers, and community publications wait for the reader's meta.
  const serverRendersComments =
    !isDraft &&
    !isPostTemplatePreview &&
    post.comments_enabled &&
    post.audience !== 'premium' &&
    !(publication.community_enabled && publication.has_community);

  // When previewing a post via Swarm, a jwt token is sent with payload tier_id or subscriber_id
  // Make a request to get a preview subscription based on the payload of the jwt token
  // The subscription in the response is used in `PostContent` component to
  // render the content based on visibility settings
  const [paywall, page, previewSubscription, firstCommentsPage] = await Promise.all([
    isPremiumPost && !post.force_premium ? getPaywall(request, post.paywall_id ?? undefined) : Promise.resolve(null),
    pagePromise,
    previewJwtToken && isDraft ? getPreviewSubscription(request, session, previewJwtToken) : Promise.resolve(undefined),
    serverRendersComments ? commentsPromise : Promise.resolve(null),
  ]);

  // getRenderableFontsCss depends on page — must stay sequential
  const { fontsCss: renderableFontsCss, adobeFontUrls } = await getRenderableFontsCss(
    site,
    (page as Page) || undefined,
    post.post_theme as PostTheme,
    getPostThemeFontFamilies(selectStandardPaywall(paywall)?.theme),
  );

  // Note: set max-age to 3 to allow prefetch caching before trying to get from CDN via s-max-age
  // For post template previews, disable caching entirely
  const cacheHeaders = isPostTemplatePreview
    ? { 'Cache-Control': 'no-store' }
    : setCacheHeaders({
        maxAge: 3,
        sMaxAge: 60 * 60 * 24 * 30, // 30 days
        staleWhileRevalidate: 60,
        publication,
        tags: [`post-${post.id}`, `pub-${publication.id}`],
      });

  const conditionalHeaders: Record<string, string> = isCacheableResponse(cacheHeaders)
    ? { [CONDITIONAL_MARKER_HEADER]: '1' }
    : {};

  return Response.json(
    {
      previewSubscription,
      previewJwtToken,
      post,
      postMergeTags: {},
      page,
      commentsEnabled: post.comments_enabled,
      recommendedReadingEnabled: publication.web_template.enable_recommended_reading,
      requestUrl: url.href,
      onlyRenderComments: showComments,
      slugInParams: params.slug || '',
      isDraft,
      isPostTemplatePreview,
      postTemplateId,
      paywall,
      currentSubscriberId,
      tierIds,
      dynamicContentOverrides,
      renderableFontsCss,
      adobeFontUrls,
      postSchemaType: site.viewable_site_version?.settings?.post_schema_type || null,
      firstCommentsPage:
        firstCommentsPage && countComments(firstCommentsPage.comments) <= SERVER_RENDERED_COMMENTS_LIMIT
          ? {
              comments: firstCommentsPage.comments,
              pagination: firstCommentsPage.pagination,
              order: site.viewable_site_version?.settings?.comments_ordering,
            }
          : null,
      postMetaInformation: {
        comments_enabled: post.comments_enabled,
        hide_premium_post: post.hide_premium_post,
        paywall_shown: post.paywall_shown,
        // metered_paywall_shown and metered_usage_shown are intentionally omitted here.
        // The server response is CDN-cached (unauthenticated), so these values are not
        // personalized. Including them would cause a flash: the modal appears during
        // hydration then disappears once the client loader fetches the real value via
        // /post_meta_information with the user's session cookie.
        // The client loader receives the raw server post values directly and uses them
        // as its fallback, so error handling is unaffected.
        force_premium: post.force_premium,
        enforce_paywall_breaks: post.enforce_paywall_breaks,
        contains_subscriber_break: post.contains_subscriber_break,
      },
    },
    {
      headers: { ...cacheHeaders, ...conditionalHeaders },
    },
  );
};

export const clientLoader = withLazyBlocks(async ({ request, serverLoader }: ClientLoaderFunctionArgs) => {
  const serverData = await serverLoader<PostLoaderData>();
  if (!serverData?.page || !serverData?.post) {
    // show the refresh page message if we didn't get server data
    throw new Error('RefreshPageError');
  }

  // For post template previews, use the template ID; otherwise use the post ID
  const postOrTemplateId = serverData.postTemplateId || serverData.post.id;
  // For post template previews, treat as draft so Rails controllers use PostTemplate lookup
  const isDraftOrTemplatePreview = serverData.isDraft || serverData.isPostTemplatePreview;
  // Use slug in params as it can differ from the one in the URL
  // when using a draft slug and previewing a draft post
  const postSlug = serverData.isDraft ? serverData.slugInParams : serverData.post.slug;

  const [postMergeTags, postMetaInformation, contentGateResult] = await Promise.all([
    fetchMergeTags({
      postId: postOrTemplateId,
      isDraft: isDraftOrTemplatePreview,
      previewJwtToken: serverData.previewJwtToken,
    }),
    fetchPostMetaInformation({
      slug: postSlug,
      isDraft: isDraftOrTemplatePreview,
      requestUrl: request.url,
      cookies: document.cookie,
      commentsEnabled: serverData.commentsEnabled,
      hidePremiumPost: serverData.post.hide_premium_post,
      paywallShown: serverData.post.paywall_shown,
      meteredUsageShown: serverData.post.metered_usage_shown,
      forcePremium: serverData.post.force_premium,
      enforcePaywallBreaks: serverData.post.enforce_paywall_breaks,
      containsSubscriberBreak: serverData.post.contains_subscriber_break,
      previewSubscriberId: serverData.previewSubscription?.subscriber_id,
      // Swarm authorizes + sources the simulation from this token; the tier/override
      // values below are ignored server-side unless backed by it.
      previewJwtToken: serverData.previewJwtToken,
      // Give precedence to current_subscriber_id in params as in post editor preview
      // it is not possible to select a tier or a condition if a subscriber
      // is selected
      previewTierIds: !serverData.currentSubscriberId ? (serverData.previewSubscription?.tier_ids ?? []) : [],
      dynamicContentOverrides: !serverData.currentSubscriberId
        ? (serverData.previewSubscription?.dynamic_content_overrides ?? [])
        : [],
    }),
    processContentGate({
      page: serverData.page as Page,
      requestUrl: request.url,
      cookies: document.cookie,
    }),
  ]);

  const { isContentClipped, clippedPageContent, subscription } = contentGateResult;

  return {
    ...serverData,
    postMetaInformation,
    postMergeTags,
    isContentClipped,
    clippedPageContent,
    subscription,
  };
});

clientLoader.hydrate = true;

export default function Post() {
  const { site, subscription } = useRouteLoaderData('root') as RootLoaderData;
  const {
    post,
    page,
    commentsEnabled,
    onlyRenderComments,
    isDraft,
    isPostTemplatePreview,
    paywall: paywallResponse,
    previewSubscription,
    postMetaInformation,
    postMergeTags,
    firstCommentsPage,
  } = useTypedLoaderData<PostLoaderData>();
  if (!site || !post) {
    // show the refresh page message if we didn't get server data
    throw new Error('RefreshPageError');
  }

  // Resolve the right paywall using the personalized metered-limit signal so a
  // subscriber over the limit sees the standard paywall instead of the metered
  // email-capture modal (re-asking their email trips the /subscribe rate limit).
  const paywall = selectPaywall(paywallResponse, postMetaInformation?.metered_paywall_limit_exceeded);

  const { cookies, setCookie, cookiesSet } = useOutletContext<OutletContext>();

  // Create Page View - skip for post template previews
  const visitToken = cookies.visit_token ?? '';
  const pageViews = cookies.page_view ?? '';
  useCreatePageView({
    visitToken: visitToken,
    pageViews: pageViews || '',
    post,
    setCookie,
    cookiesSet,
    skip: isPostTemplatePreview,
  });

  useSetLastViewedResource({
    guid: `Post:${post.id}`,
    setCookie,
    cookiesSet,
    skip: isPostTemplatePreview,
  });

  // setup click tracking for links - skip for post template previews
  useClickTracking({ post, visitToken, skip: isPostTemplatePreview });
  useMetaViewContent(isPostTemplatePreview ? null : post.id, {
    content_type: 'article',
    content_ids: [post.id],
    content_name: post.web_title,
  });

  return (
    <PostDataProvider
      post={post}
      onlyRenderComments={onlyRenderComments}
      isDraft={isDraft}
      postMergeTags={postMergeTags}
      paywall={paywall}
      previewSubscription={previewSubscription}
      postMetaInformation={postMetaInformation}
      firstCommentsPage={firstCommentsPage}
    >
      <PostPageProvider
        post={post}
        site={site}
        hide_premium_post={postMetaInformation?.hide_premium_post}
        paywall_shown={postMetaInformation?.paywall_shown}
        metered_usage_shown={postMetaInformation?.metered_usage_shown}
        force_premium={postMetaInformation?.force_premium}
        enforce_paywall_breaks={postMetaInformation?.enforce_paywall_breaks}
        contains_subscriber_break={postMetaInformation?.contains_subscriber_break}
        subscription={subscription}
        previewSubscription={previewSubscription}
        commentsEnabled={postMetaInformation?.comments_enabled ?? commentsEnabled}
        isDraft={isDraft}
        onlyRenderComments={onlyRenderComments}
        paywall={paywall}
        community_comments={postMetaInformation?.community_comments}
        meta_information_fetched={Boolean(postMetaInformation?._fetched)}
      >
        <PostPageContext.Consumer>
          {(value) => {
            return (
              <>
                {!value?.onlyRenderComments && <StocktwitsEmbed />}
                <StaticPage site={site} page={page}>
                  {!value?.onlyRenderComments && (
                    <>
                      {/* Keyed so post-to-post navigation retries widgets.js after a failed load. */}
                      <TwitterEmbed key={post.id} />
                      <BlueskyEmbed updateOn />
                    </>
                  )}
                </StaticPage>
              </>
            );
          }}
        </PostPageContext.Consumer>
      </PostPageProvider>
    </PostDataProvider>
  );
}

export const shouldRevalidate = shouldRevalidateLoaderData;
